import * as React from 'react'
import * as Lodash from 'lodash/fp'
import { Button, Tooltip, styled, useTheme } from '@design/pt-ui'
const StyledButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  flex: 'auto',
  alignItems: 'center',
  padding: theme.spacing(0.5),
  margin: '0 !important',
  borderRadius: 0,
  borderLeft: `1px solid rgba(0, 0, 0, 0.23)`,
  borderTop: `1px solid rgba(0, 0, 0, 0.23)`,
  borderBottom: `1px solid rgba(0, 0, 0, 0.23)`,
  textAlign: 'center',
  textTransform: 'none',
  color: theme.palette.custom.button.light,
  cursor: 'pointer',
  transition: '0.3s',
  '&:first-of-type': {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  '&:last-of-type': {
    borderRight: `1px solid rgba(0, 0, 0, 0.23)`,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  '&:hover': {
    borderLeft: `1px solid rgba(0, 0, 0, 0.87)`,
    borderTop: `1px solid rgba(0, 0, 0, 0.87)`,
    borderBottom: `1px solid rgba(0, 0, 0, 0.87)`,
    '&:last-of-type': {
      borderRight: `1px solid rgba(0, 0, 0, 0.87)`,
    },
  },
  '&.active': {
    color: theme.palette.custom.button.dark,
  },
  '&.error': {
    borderLeft: `2px solid ${theme.palette.custom.gray.dark}`,
    borderTop: `2px solid ${theme.palette.custom.gray.dark}`,
    borderBottom: `2px solid ${theme.palette.custom.gray.dark}`,
    color: theme.palette.custom.button.main,
    '&:last-of-type': {
      borderRight: `2px solid ${theme.palette.custom.gray.dark}`,
    },
  },
}))

interface IOptionProps<T> extends IOption<T> {
  onClick: (value: T) => void
  error: boolean
  selected: boolean
  disabled: boolean
}

export interface IOption<T> {
  value: T
  label: string
  disabled?: boolean
  tooltip?: string
}

function Option<T>(props: IOptionProps<T>) {
  const theme = useTheme()

  const handleClick = () => {
    if (props.disabled) {
      return
    }

    props.onClick(props.value)
  }

  const { error, label, disabled, selected } = props

  const style = Lodash.cond([
    [
      (_) => disabled,
      (_) => ({
        backgroundColor: theme.palette.custom.gray.light,
        cursor: 'not-allowed',
        color: theme.palette.custom.button.contrastText,
      }),
    ],
    [
      (_) => selected,
      (_) => ({
        backgroundColor: theme.palette.custom.button.light,
        color: theme.palette.custom.button.contrastText,
      }),
    ],
    [
      (_) => !selected,
      (_) => ({
        backgroundColor: theme.palette.custom.button.contrastText,
        color: theme.palette.custom.gray.dark,
      }),
    ],
    [
      (_) => error,
      (_) => ({
        backgroundColor: theme.palette.error.main,
        color: theme.palette.custom.button.contrastText,
      }),
    ],

    [
      (_) => true,
      (_) => ({
        backgroundColor: theme.palette.custom.button.main,
        color: theme.palette.custom.button.contrastText,
      }),
    ],
  ])('')

  const button = (
    <StyledButton sx={style} onClick={handleClick}>
      {label}
    </StyledButton>
  )

  return props.tooltip ? (
    <Tooltip title={props.tooltip} placement="top">
      {button}
    </Tooltip>
  ) : (
    button
  )
}

export default Option
